import * as THREE from 'three';
import { RIDES } from '../content/rides';
import type { Random } from '../core/Random';
import { tubeMan } from './builders/buildings';
import { neonBeerSign } from './builders/furniture';
import { pennants, signBoard, tireStack, trashCan } from './builders/props';
import { buildRide } from './builders/rides';
import { buildTrailer } from './builders/trailer';
import type { WorldKit } from './districts';
import { CAR_LOT, SPOTS } from './layout';
import { box, cyl, M, mat, mesh, registerNightGlow, texMat, worldBox } from './materials';
import { sidingTexture, signTexture, woodTexture } from './textures';

/**
 * The south strip past the Gas-N-Grub: the Booze Barn, the Rusty Spur, and down the
 * other way, Cooter's Used Cars. Buildings face north, toward the highway.
 */

let neonMat: THREE.MeshStandardMaterial | null = null;
function windowGlow(): THREE.MeshStandardMaterial {
  if (!neonMat) {
    neonMat = new THREE.MeshStandardMaterial({
      color: '#8a7a62',
      emissive: new THREE.Color('#ffb060'),
      emissiveIntensity: 0,
      roughness: 0.3,
    });
    registerNightGlow(neonMat, 1.2);
  }
  return neonMat;
}

/** A red barn with a gambrel roof, a drive-thru window and a lot of beer. */
function boozeBarn(): THREE.Group {
  const g = new THREE.Group();
  const W = 12;
  const D = 8;
  const H = 3.6;
  const red = texMat(sidingTexture(), { color: '#a3302a' });
  g.add(mesh(worldBox(W, H, D, 2), red, 0, H / 2, 0));
  g.add(box(W + 0.1, 0.25, D + 0.1, M.cinder, 0, 0.12, 0, { cast: false }));
  // Gambrel roof: the barn-shaped part.
  const shape = new THREE.Shape();
  shape.moveTo(-D / 2 - 0.3, 0);
  shape.lineTo(-D / 2 + 0.6, 1.5);
  shape.lineTo(0, 2.5);
  shape.lineTo(D / 2 - 0.6, 1.5);
  shape.lineTo(D / 2 + 0.3, 0);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: W + 0.4, bevelEnabled: false });
  geo.translate(0, 0, -(W + 0.4) / 2);
  const roof = mesh(geo, mat('#5a5652', { flat: true, rough: 0.8 }), 0, H + 0.005, 0);
  roof.rotation.y = Math.PI / 2;
  g.add(roof);
  // Gable ends.
  for (const s of [-1, 1]) {
    const gable = new THREE.Shape();
    gable.moveTo(-D / 2, 0);
    gable.lineTo(-D / 2 + 0.75, 1.42);
    gable.lineTo(0, 2.38);
    gable.lineTo(D / 2 - 0.75, 1.42);
    gable.lineTo(D / 2, 0);
    gable.closePath();
    const gm = mesh(new THREE.ShapeGeometry(gable), mat('#a3302a'), s * (W / 2 + 0.005), H, 0);
    gm.rotation.y = (s * Math.PI) / 2;
    g.add(gm);
  }
  const front = D / 2;
  // White barn-door trim with the X.
  const white = mat('#f2efe6');
  g.add(box(2.4, 2.6, 0.06, mat('#7a231e'), 0, 1.3, front + 0.03));
  for (const x of [-1.2, 1.2]) g.add(box(0.14, 2.6, 0.06, white, x, 1.3, front + 0.07, { cast: false }));
  for (const y of [0.07, 2.6]) g.add(box(2.4, 0.14, 0.06, white, 0, y, front + 0.07, { cast: false }));
  for (const t of [-1, 1]) {
    const x = box(0.12, 3.4, 0.05, white, 0, 1.3, front + 0.09, { cast: false });
    x.rotation.z = t * 0.73;
    g.add(x);
  }
  // Windows full of neon.
  for (const x of [-4, 4]) {
    g.add(box(2.2, 1.3, 0.05, white, x, 1.8, front + 0.03, { cast: false }));
    g.add(mesh(new THREE.PlaneGeometry(2.0, 1.1), windowGlow(), x, 1.8, front + 0.06, { cast: false }));
    const neon = neonBeerSign();
    neon.scale.setScalar(0.85);
    neon.position.set(x, 1.85, front + 0.08);
    g.add(neon);
  }
  const sign = signTexture({
    w: 1024,
    h: 256,
    bg: '#f4e9cf',
    fg: '#7a1f17',
    border: '#3d2416',
    borderWidth: 14,
    gap: 2,
    lines: [
      { text: 'BOOZE BARN', size: 140, font: 'Rye' },
      { text: 'COLD BEER • LIQUOR • LOTTO • DRIVE THRU', size: 44, font: 'Anton', color: '#2b2b2b' },
    ],
  });
  g.add(box(6.2, 1.6, 0.12, M.pole, 0, H + 1.0, front + 0.3));
  g.add(mesh(new THREE.PlaneGeometry(6.0, 1.5), texMat(sign), 0, H + 1.0, front + 0.37, { cast: false }));
  // Drive-thru window on the east side.
  g.add(box(0.06, 1.0, 1.4, white, W / 2 + 0.03, 1.5, 1.6, { cast: false }));
  g.add(
    mesh(new THREE.PlaneGeometry(1.2, 0.85), windowGlow(), W / 2 + 0.07, 1.5, 1.6, { cast: false }).rotateY(
      Math.PI / 2,
    ),
  );
  return g;
}

/** A roadhouse bar: rough boards, a porch, neon in the windows and a sign you can see from the highway. */
function rustySpur(): THREE.Group {
  const g = new THREE.Group();
  const W = 16;
  const D = 10;
  const H = 4;
  const boards = texMat(woodTexture(), { color: '#c4956a' });
  g.add(mesh(worldBox(W, H, D, 1.5), boards, 0, H / 2, 0));
  g.add(box(W + 0.6, 0.35, D + 0.6, mat('#3d3a36'), 0, H + 0.17, 0));
  g.add(box(W + 0.1, 0.25, D + 0.1, M.cinder, 0, 0.12, 0, { cast: false }));
  // False front, like an old saloon.
  g.add(mesh(worldBox(W, 1.6, 0.25, 1.5), boards, 0, H + 0.8, D / 2 - 0.12));
  const front = D / 2;
  // Porch with posts and a tin roof.
  g.add(mesh(worldBox(W, 0.15, 2.4, 1), texMat(woodTexture()), 0, 0.3, front + 1.2));
  for (const x of [-7.6, -3.8, 3.8, 7.6]) g.add(box(0.18, 2.9, 0.18, M.trunk, x, 1.75, front + 2.25));
  const tin = box(W + 0.3, 0.06, 2.6, mat('#8d8f8a', { metal: 0.5, rough: 0.5 }), 0, 3.25, front + 1.25);
  tin.rotation.x = 0.12;
  g.add(tin);
  // Swinging doors.
  g.add(box(1.8, 2.3, 0.05, mat('#24180f'), 0, 1.5, front + 0.03));
  for (const s of [-1, 1])
    g.add(box(0.85, 1.1, 0.06, mat('#9a6a3e'), s * 0.45, 1.5, front + 0.07, { cast: false }));
  // Windows with neon.
  for (const x of [-5, 5]) {
    g.add(box(2.4, 1.4, 0.05, mat('#2a1d14'), x, 2.0, front + 0.03, { cast: false }));
    g.add(mesh(new THREE.PlaneGeometry(2.2, 1.2), windowGlow(), x, 2.0, front + 0.06, { cast: false }));
    const neon = neonBeerSign();
    neon.position.set(x, 2.05, front + 0.08);
    g.add(neon);
  }
  const name = signTexture({
    w: 1024,
    h: 200,
    bg: '#1b1410',
    fg: '#ff9a3c',
    border: '#ff9a3c',
    borderWidth: 8,
    lines: [{ text: 'THE RUSTY SPUR', size: 150, font: 'Rye' }],
  });
  const nameMat = new THREE.MeshStandardMaterial({
    map: name,
    emissive: new THREE.Color('#ffffff'),
    emissiveMap: name,
    emissiveIntensity: 0,
  });
  registerNightGlow(nameMat, 1.4);
  g.add(mesh(new THREE.PlaneGeometry(9, 1.4), nameMat, 0, H + 0.8, front + 0.02, { cast: false }));
  // Bare bulbs along the porch roof.
  const bulb = new THREE.MeshStandardMaterial({ color: '#fff3c4', emissive: new THREE.Color('#ffc870') });
  registerNightGlow(bulb, 2);
  for (let x = -7; x <= 7; x += 1.4)
    g.add(mesh(new THREE.SphereGeometry(0.08, 6, 4), bulb, x, 3.05, front + 2.3, { cast: false }));
  // A busted ice machine and a bench by the door.
  g.add(box(1.0, 1.6, 0.8, mat('#d9dde0', { metal: 0.3 }), -2.4, 1.18, front + 0.6));
  g.add(box(1.6, 0.08, 0.45, texMat(woodTexture()), 2.6, 0.82, front + 0.5));
  for (const x of [1.9, 3.3]) g.add(box(0.08, 0.45, 0.4, M.black, x, 0.6, front + 0.5));
  return g;
}

/** The roadside sign: on a pole, lit up at night. */
function poleSign(lines: Parameters<typeof signTexture>[0], w: number, h: number, pole: number): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.16, 0.2, pole, M.steel, 0, pole / 2, 0, 8));
  g.add(box(w + 0.2, h + 0.2, 0.25, mat('#2b2b2b'), 0, pole + h / 2, 0));
  const tex = signTexture(lines);
  const m = new THREE.MeshStandardMaterial({
    map: tex,
    emissive: new THREE.Color('#ffffff'),
    emissiveMap: tex,
    emissiveIntensity: 0,
  });
  registerNightGlow(m, 0.9);
  for (const s of [-1, 1]) {
    const face = mesh(new THREE.PlaneGeometry(w, h), m, 0, pole + h / 2, s * 0.135, { cast: false });
    face.rotation.y = s > 0 ? 0 : Math.PI;
    g.add(face);
  }
  return g;
}

export function buildWestStrip(kit: WorldKit, rng: Random): void {
  // Booze Barn.
  const b = SPOTS.boozeBarn;
  kit.place(boozeBarn(), b.x, b.z, Math.PI);
  kit.solid(b.x, b.z, 12, 8, 6);
  // The Rusty Spur.
  const r = SPOTS.rustySpur;
  kit.place(rustySpur(), r.x, r.z, Math.PI);
  kit.solid(r.x, r.z, 16, 10, 5.6);
  // Porch posts (rotated with the building, so local x flips).
  for (const x of [-7.6, -3.8, 3.8, 7.6]) kit.collision.addCircle(r.x - x, r.z - 7.25, 0.15);
  const spurSign = poleSign(
    {
      w: 640,
      h: 384,
      bg: '#1b1410',
      fg: '#ff9a3c',
      border: '#ff9a3c',
      borderWidth: 10,
      gap: 6,
      lines: [
        { text: 'RUSTY SPUR', size: 110, font: 'Rye' },
        { text: 'COLD BEER • POOL • DARTS', size: 40, font: 'Anton', color: '#5ff3ff' },
        { text: 'MECHANICAL BULL', size: 52, font: 'Bangers', color: '#ff4fa3' },
        { text: 'NO FIGHTIN’ (OUTSIDE)', size: 34, font: 'Anton', color: '#f2efe6' },
      ],
    },
    3.2,
    1.9,
    4.2,
  );
  kit.place(spurSign, r.x + 9.5, 69.5);
  kit.collision.addCircle(r.x + 9.5, 69.5, 0.25);
  // Parked out front: motorcycles and a couple of trucks.
  for (const [x, rot] of [
    [-99.5, 0.2],
    [-98, 0.2],
    [-96.5, 0.25],
  ] as const) {
    const bike = motorcycle(rng);
    kit.place(bike, x, 75.5, rot + Math.PI / 2);
  }
  const truck = buildRide('lifted');
  truck.group.traverse((o) => (o.userData.noBatch = false));
  kit.place(truck.group, -76, 73.5, Math.PI / 2);
  kit.collision.addRect(-76, 73.5, 2.4, 5.6);
  for (const x of [-72, -50]) {
    kit.place(trashCan(rng), x, 79.2);
    kit.collision.addCircle(x, 79.2, 0.35);
  }
  // Ice and propane cage by the Booze Barn door, and lot lines.
  for (let x = -104; x <= -50; x += 3.2)
    kit.root.add(box(0.12, 0.01, 3.5, mat('#e8e6dc'), x, 0.035, 76, { cast: false }));
}

function motorcycle(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const paint = mat(rng.pick(['#111111', '#7a1f2b', '#2b4f8a']), { metal: 0.5, rough: 0.3 });
  for (const x of [-0.7, 0.75]) {
    const w = cyl(0.33, 0.33, 0.12, M.rubber, x, 0.33, 0, 14);
    w.rotation.x = Math.PI / 2;
    g.add(w);
  }
  g.add(box(0.9, 0.3, 0.3, paint, 0, 0.7, 0));
  g.add(box(0.5, 0.12, 0.3, M.black, -0.35, 0.86, 0));
  g.add(cyl(0.02, 0.02, 0.8, M.chrome, 0.7, 0.95, 0, 6).rotateX(Math.PI / 2));
  g.add(cyl(0.03, 0.03, 0.6, M.chrome, 0.62, 0.6, 0, 6).rotateZ(-0.5));
  g.add(box(0.7, 0.08, 0.08, M.chrome, -0.2, 0.4, 0.18));
  g.rotation.x = 0.12;
  return g;
}

/** Cooter's Used Cars: pennants, a tube man, a little office and everything for sale. */
export function buildCarLot(kit: WorldKit, rng: Random): { tube: ReturnType<typeof tubeMan> } {
  const L = CAR_LOT;
  // Pennant strings around the lot.
  const cols = ['#c62828', '#ffffff', '#1d4e9e', '#ffd400'];
  const posts = [
    new THREE.Vector3(L.minX + 1, 4, L.minZ + 1.5),
    new THREE.Vector3(L.maxX - 1, 4, L.minZ + 1.5),
    new THREE.Vector3(L.maxX - 1, 4, L.maxZ - 1),
    new THREE.Vector3(L.minX + 1, 4, L.maxZ - 1),
  ];
  for (const p of posts) {
    kit.root.add(cyl(0.06, 0.06, 4, M.steel, p.x, 2, p.z, 6));
    kit.collision.addCircle(p.x, p.z, 0.12);
  }
  kit.root.add(
    pennants(posts[0]!, posts[1]!, cols),
    pennants(posts[0]!, posts[3]!, cols),
    pennants(posts[1]!, posts[2]!, cols),
  );
  // The sign.
  const sign = signBoard(
    {
      w: 1024,
      h: 448,
      bg: '#ffd400',
      fg: '#c62828',
      border: '#111111',
      borderWidth: 18,
      gap: 6,
      lines: [
        { text: "COOTER'S", size: 150, font: 'Bangers' },
        { text: 'USED CARS & TRUCKS', size: 70, font: 'Anton', color: '#111' },
        { text: 'WE TOTE THE NOTE • NO CREDIT? NO PROBLEM', size: 46, font: 'Anton', color: '#111' },
        { text: 'nothing economical, nothing normal', size: 34, font: 'Anton', color: '#555' },
      ],
    },
    4.4,
    1.95,
    2.0,
  );
  const s = SPOTS.carLotSign;
  kit.place(sign, s.x, s.z, Math.PI);
  kit.collision.addRect(s.x, s.z, 4.4, 0.4);
  // Office: a single-wide with a sign on it.
  const o = SPOTS.cooterOffice;
  const office = buildTrailer({
    length: 9,
    width: 3.6,
    color: '#f4ecc4',
    stripe: '#c62828',
    doorX: -2.6,
    windows: [
      { x: 0.8, side: 1, kind: 'blinds' },
      { x: 3, side: 1, kind: 'glass' },
    ],
    skirting: 'metal',
    steps: 'blocks',
    seed: 44,
  });
  kit.place(office, o.x, o.z, Math.PI);
  kit.solid(o.x, o.z, 9, 3.6, 3.2);
  kit.collision.addRect(o.x + 2.6, o.z - 2.25, 1.0, 0.9);
  const tag = signTexture({
    w: 512,
    h: 128,
    bg: '#ffffff',
    fg: '#c62828',
    lines: [{ text: 'OFFICE • COOTER IS IN', size: 64, font: 'Anton' }],
  });
  const plate = mesh(new THREE.PlaneGeometry(2.6, 0.65), texMat(tag), o.x - 0.6, 2.9, o.z - 1.86, {
    cast: false,
  });
  plate.rotation.y = Math.PI;
  kit.root.add(plate);
  // Everything for sale, in two rows facing the highway, with prices on the windshields.
  const forSale = RIDES.filter((r) => r.price > 0);
  forSale.forEach((def, i) => {
    const row = i < 4 ? 0 : 1;
    const x = L.minX + 6 + (i % 4) * 8.6 + row * 3;
    const z = row === 0 ? 74 : 82;
    const car = buildRide(def.id);
    car.group.traverse((obj) => (obj.userData.noBatch = false));
    kit.place(car.group, x, z, Math.PI / 2 + 0.25);
    kit.collision.addRect(x, z, car.width + 0.6, car.length * 0.95);
    const price = signTexture({
      w: 256,
      h: 160,
      bg: '#fff36b',
      fg: '#c62828',
      border: '#c62828',
      borderWidth: 6,
      gap: 0,
      lines: [
        { text: `$${def.price.toLocaleString()}`, size: 82, font: 'Bangers' },
        { text: 'OBO', size: 40, font: 'Anton', color: '#111' },
      ],
    });
    const p = mesh(new THREE.PlaneGeometry(0.9, 0.56), texMat(price), x, 0.4, z, { cast: false });
    p.position.set(x - Math.sin(0.25) * 0.2, 0.25, z - car.length / 2 - 0.35);
    p.rotation.y = Math.PI;
    kit.root.add(p);
    kit.root.add(box(0.06, 0.25, 0.06, M.pole, p.position.x, 0.12, p.position.z + 0.05));
  });
  kit.place(tireStack(rng, 6), L.maxX - 4, L.minZ + 4);
  kit.collision.addCircle(L.maxX - 4, L.minZ + 4, 0.5);
  const tube = tubeMan('#2b6cb0');
  kit.place(tube.group, L.minX + 3, L.minZ + 4, 0, kit.dynamic);
  kit.collision.addCircle(L.minX + 3, L.minZ + 4, 0.5);
  return { tube };
}
